import uniq from 'lodash/uniq';

import {
  sortTripViewModelBasedOnPrice,
  sortTripViewModelsBasedOnScoreLogic,
  sortTripViewModelsBasedOnDepartureTimeLogic,
} from '@helpers/flightSort';

import { FlightSortType } from '@wegoTypes/flights/flightSortType';
import { TripViewModel } from '@wegoTypes/flights/tripViewModel';
import { SortOrder } from '@wegoTypes/sortOrder';

import {
  parseFilterValuesFromSearchParam,
  frameFilterParamsFromFilterValues,
} from './flightPerLegSearchFilter';
import { DepartLegGroup } from './types';

export function copySortParamsFromDepartLegToReturnLeg(searchParams: URLSearchParams) {
  ['sort', 'order'].forEach((sortParamName) => {
    const sortParams = searchParams.get(sortParamName);

    if (sortParams) {
      searchParams.delete(sortParamName);

      const sortValues = parseFilterValuesFromSearchParam(sortParams);
      const copiedSortValues = [
        sortValues[0],
        sortValues[0].includes('leg1_departure_time') ? ['leg2_departure_time'] : sortValues[0],
      ];

      if (copiedSortValues[0].length || copiedSortValues[1].length) {
        const updatedFilterParams = frameFilterParamsFromFilterValues(copiedSortValues);

        searchParams.append(sortParamName, updatedFilterParams);
      }
    }
  });

  return searchParams;
}

export function removeReturnLegSortParams(searchParams: URLSearchParams) {
  ['sort', 'order'].forEach((sortParamName) => {
    const sortParams = searchParams.get(sortParamName);

    if (sortParams) {
      const sortValues = parseFilterValuesFromSearchParam(sortParams);

      const updatedSortValues = [sortValues[0], []];

      if (updatedSortValues[0].length) {
        const updatedSortParams = frameFilterParamsFromFilterValues(updatedSortValues);

        searchParams.set(sortParamName, updatedSortParams);
      }
    }
  });
}

export function getSortOrderOfTripViewModelsBasedOnPrice(
  aTripViewModel: TripViewModel,
  bTripViewModel: TripViewModel,
) {
  const aTripViewModelFareWithFee = aTripViewModel.filteredFares[0]?.fare?.price.amountUsd;
  const bTripViewModelFareWithFee = bTripViewModel.filteredFares[0]?.fare?.price.amountUsd;

  return aTripViewModelFareWithFee - bTripViewModelFareWithFee;
}

export function sortTripViewModelsBasedOnFareScore(tripViewModels: TripViewModel[]) {
  return tripViewModels
    .slice()
    .sort((aTripViewModel: TripViewModel, bTripViewModel: TripViewModel) => {
      const sortByScore = sortTripViewModelsBasedOnScoreLogic(aTripViewModel, bTripViewModel);

      if (sortByScore !== 0) {
        return sortByScore;
      }

      const sortByPrice = getSortOrderOfTripViewModelsBasedOnPrice(aTripViewModel, bTripViewModel);

      if (sortByPrice !== 0) {
        return sortByPrice;
      }

      const sortByDepartureTime = sortTripViewModelsBasedOnDepartureTimeLogic(
        aTripViewModel,
        bTripViewModel,
      );

      if (sortByDepartureTime !== 0) {
        return sortByDepartureTime;
      }

      return 0;
    });
}

export function sortGroupedTripViewModelBasedOnDepartLegByScore(
  tripViewModelGroupedByDepartLeg: Array<DepartLegGroup>,
  numberOfDecimalsInCurrency: number = 0,
) {
  const groupWiseSortedDepartLegGroup = tripViewModelGroupedByDepartLeg.map((departLegGroup) => {
    return {
      departLegId: departLegGroup.departLegId,
      tripViewModels: sortTripViewModelsBasedOnFareScore(departLegGroup.tripViewModels),
    };
  });

  const sortedDepartLegGroups = groupWiseSortedDepartLegGroup
    .slice()
    .sort((aDepartLegGroup: DepartLegGroup, bDepartLegGroup: DepartLegGroup) => {
      const aTripViewModel = aDepartLegGroup.tripViewModels[0];
      const bTripViewModel = bDepartLegGroup.tripViewModels[0];

      const sortByScore = sortTripViewModelsBasedOnScoreLogic(aTripViewModel, bTripViewModel);

      if (sortByScore !== 0) {
        return sortByScore;
      }

      const sortByPrice = getSortOrderOfTripViewModelsBasedOnPrice(aTripViewModel, bTripViewModel);

      if (sortByPrice !== 0) {
        return sortByPrice;
      }

      const sortByDepartureTime = sortTripViewModelsBasedOnDepartureTimeLogic(
        aTripViewModel,
        bTripViewModel,
      );

      if (sortByDepartureTime !== 0) {
        return sortByDepartureTime;
      }

      return 0;
    });

  if (sortedDepartLegGroups.length) {
    const bestTripViewModel: TripViewModel = {
      ...sortedDepartLegGroups[0].tripViewModels[0],
      tripCardTags: ['best'],
    };

    sortedDepartLegGroups[0].tripViewModels.splice(0, 1, bestTripViewModel);
  }

  return highlightCheapestDepartLegGroups(sortedDepartLegGroups, numberOfDecimalsInCurrency);
}

function highlightCheapestDepartLegGroups(
  departLegGroups: Array<DepartLegGroup>,
  numberOfDecimalsInCurrency: number,
): Array<DepartLegGroup> {
  if (departLegGroups.length) {
    const priceSortedDepartLegGroups =
      sortGroupedTripViewModelBasedOnDepartLegByPrice(departLegGroups);

    const cheapestDepartLegGroup = priceSortedDepartLegGroups[0];

    const cheapestDepartLegGroupIndex = departLegGroups.findIndex(
      (departLegGroup) => departLegGroup.departLegId === cheapestDepartLegGroup.departLegId,
    );

    const insertIndexOfCheapestTripViewModel = cheapestDepartLegGroupIndex === 0 ? 0 : 1;

    const cheapestTripViewModel: TripViewModel = {
      ...cheapestDepartLegGroup.tripViewModels[0],
      tripCardTags: insertIndexOfCheapestTripViewModel === 0 ? ['best', 'cheapest'] : ['cheapest'],
    };

    cheapestDepartLegGroup.tripViewModels[0] = cheapestTripViewModel;

    departLegGroups.splice(cheapestDepartLegGroupIndex, 1);
    departLegGroups.splice(insertIndexOfCheapestTripViewModel, 0, cheapestDepartLegGroup);

    const cheapestDirectDepartLegGroup = priceSortedDepartLegGroups.find(
      (departLegGroup) => departLegGroup.tripViewModels[0].legs[0].leg.segments.length === 1,
    );

    const cheapestPrice =
      cheapestTripViewModel.filteredFares[0]?.fare?.price.amountWithFraction || 0;
    const cheapestPriceWithDecimals = +cheapestPrice.toFixed(numberOfDecimalsInCurrency);

    if (!!cheapestDirectDepartLegGroup) {
      const cheapestDirectDepartLegGroupIndex = departLegGroups.findIndex(
        (departLegGroup) => departLegGroup.departLegId === cheapestDirectDepartLegGroup.departLegId,
      );

      const insertIndexOfCheapestDirectTripViewModel =
        cheapestDirectDepartLegGroupIndex < 2
          ? cheapestDirectDepartLegGroupIndex
          : insertIndexOfCheapestTripViewModel === 0
          ? 1
          : 2;

      const cheapestDirectTripViewModel: TripViewModel = {
        ...cheapestDirectDepartLegGroup.tripViewModels[0],
        tripCardTags: [
          ...(insertIndexOfCheapestDirectTripViewModel === cheapestDirectDepartLegGroupIndex
            ? departLegGroups[insertIndexOfCheapestDirectTripViewModel].tripViewModels[0]
                .tripCardTags ?? []
            : []),
          'cheapest_direct',
        ],
      };

      cheapestDirectDepartLegGroup.tripViewModels[0] = cheapestDirectTripViewModel;

      departLegGroups.splice(cheapestDirectDepartLegGroupIndex, 1);
      departLegGroups.splice(
        insertIndexOfCheapestDirectTripViewModel,
        0,
        cheapestDirectDepartLegGroup,
      );

      const cheapestDirectPrice =
        cheapestDirectTripViewModel.filteredFares[0]?.fare?.price.amountWithFraction || 0;
      const cheapestDirectPriceWithDecimals = +cheapestDirectPrice.toFixed(
        numberOfDecimalsInCurrency,
      );

      // Highlight all cheapest direct
      departLegGroups.forEach((departLegGroup) => {
        const isTripViewModelForDepartLegDirect =
          departLegGroup.tripViewModels[0].legs[0].leg.segments.length === 1;

        const tripViewModelPrice =
          departLegGroup.tripViewModels[0]?.filteredFares[0]?.fare?.price.amountWithFraction || 0;
        const tripViewModelPriceWithDecimals = +tripViewModelPrice.toFixed(
          numberOfDecimalsInCurrency,
        );

        const isCheapestTripViewModel =
          tripViewModelPriceWithDecimals === cheapestDirectPriceWithDecimals;

        if (isTripViewModelForDepartLegDirect && isCheapestTripViewModel) {
          departLegGroup.tripViewModels[0] = {
            ...departLegGroup.tripViewModels[0],
            tripCardTags: uniq([
              ...(departLegGroup.tripViewModels[0].tripCardTags || []),
              'cheapest_direct',
            ]),
          };
        }
      });
    }

    // Highlight all cheapest flights
    departLegGroups.map((departLegGroup) => {
      const tripViewModelPrice =
        departLegGroup.tripViewModels[0]?.filteredFares[0]?.fare?.price.amountWithFraction || 0;

      const tripViewModelPriceWithDecimals = +tripViewModelPrice.toFixed(
        numberOfDecimalsInCurrency,
      );

      const isCheapestTripViewModel = tripViewModelPriceWithDecimals === cheapestPriceWithDecimals;

      if (isCheapestTripViewModel) {
        departLegGroup.tripViewModels[0] = {
          ...departLegGroup.tripViewModels[0],
          tripCardTags: uniq([
            ...(departLegGroup.tripViewModels[0].tripCardTags || []),
            'cheapest',
          ]),
        };
      }
    });
  }

  return departLegGroups;
}

export function sortGroupedTripViewModelBasedOnDepartLegByPrice(
  tripViewModelGroupedByDepartLeg: Array<DepartLegGroup>,
): DepartLegGroup[] {
  const groupWiseSortedDepartLegGroup = tripViewModelGroupedByDepartLeg.map((departLegGroup) => {
    return {
      departLegId: departLegGroup.departLegId,
      tripViewModels: sortTripViewModelBasedOnPrice(departLegGroup.tripViewModels, 'asc', true),
    };
  });

  const sortedDepartLegGroups = groupWiseSortedDepartLegGroup
    .slice()
    .sort((aDepartLegGroup: DepartLegGroup, bDepartLegGroup: DepartLegGroup) => {
      const aTripViewModel = aDepartLegGroup.tripViewModels[0];
      const bTripViewModel = bDepartLegGroup.tripViewModels[0];

      const sortByPrice = getSortOrderOfTripViewModelsBasedOnPrice(aTripViewModel, bTripViewModel);

      if (sortByPrice !== 0) {
        return sortByPrice;
      }

      const sortByScore = sortTripViewModelsBasedOnScoreLogic(aTripViewModel, bTripViewModel);

      if (sortByScore !== 0) {
        return sortByScore;
      }

      const sortByDepartureTime = sortTripViewModelsBasedOnDepartureTimeLogic(
        aTripViewModel,
        bTripViewModel,
      );

      if (sortByDepartureTime !== 0) {
        return sortByDepartureTime;
      }

      return 0;
    });

  return sortedDepartLegGroups;
}

export function sortGroupedTripViewModelBasedOnDepartLegByDuration(
  tripViewModelGroupedByDepartLeg: Array<DepartLegGroup>,
) {
  const groupWiseSortedDepartLegGroup = tripViewModelGroupedByDepartLeg.map((departLegGroup) => {
    return {
      departLegId: departLegGroup.departLegId,
      tripViewModels: sortTripViewModelBasedOnPrice(departLegGroup.tripViewModels, 'asc', true),
    };
  });

  return groupWiseSortedDepartLegGroup.sort(
    (aDepartLegGroup: DepartLegGroup, bDepartLegGroup: DepartLegGroup) => {
      const aTripViewModel = aDepartLegGroup.tripViewModels[0];
      const bTripViewModel = bDepartLegGroup.tripViewModels[0];

      const aLegDuration = aTripViewModel.legs[0].leg.durationMinutes;
      const bLegDuration = bTripViewModel.legs[0].leg.durationMinutes;

      const sortByDuration = aLegDuration - bLegDuration;

      if (sortByDuration !== 0) {
        return sortByDuration;
      }

      const sortByScore = sortTripViewModelsBasedOnScoreLogic(aTripViewModel, bTripViewModel);

      if (sortByScore !== 0) {
        return sortByScore;
      }

      const sortByPrice = getSortOrderOfTripViewModelsBasedOnPrice(aTripViewModel, bTripViewModel);

      if (sortByPrice !== 0) {
        return sortByPrice;
      }

      const sortByDepartureTime = sortTripViewModelsBasedOnDepartureTimeLogic(
        aTripViewModel,
        bTripViewModel,
      );

      if (sortByDepartureTime !== 0) {
        return sortByDepartureTime;
      }

      return 0;
    },
  );
}

export function sortGroupedTripViewModelBasedOnDepartLegByDepartureTime(
  tripViewModelGroupedByDepartLeg: Array<DepartLegGroup>,
  sortOrderType: SortOrder,
) {
  const groupWiseSortedDepartLegGroup = tripViewModelGroupedByDepartLeg.map((departLegGroup) => {
    return {
      departLegId: departLegGroup.departLegId,
      tripViewModels: sortTripViewModelBasedOnPrice(departLegGroup.tripViewModels, 'asc', true),
    };
  });

  return groupWiseSortedDepartLegGroup.sort(
    (aDepartLegGroup: DepartLegGroup, bDepartLegGroup: DepartLegGroup) => {
      const aTripViewModel = aDepartLegGroup.tripViewModels[0];
      const bTripViewModel = bDepartLegGroup.tripViewModels[0];

      const sortByDepartureTime =
        (sortOrderType === 'asc' ? 1 : -1) *
        sortTripViewModelsBasedOnDepartureTimeLogic(aTripViewModel, bTripViewModel);

      if (sortByDepartureTime !== 0) {
        return sortByDepartureTime;
      }

      const sortByPrice = getSortOrderOfTripViewModelsBasedOnPrice(aTripViewModel, bTripViewModel);

      if (sortByPrice !== 0) {
        return sortByPrice;
      }

      const sortByScore = sortTripViewModelsBasedOnScoreLogic(aTripViewModel, bTripViewModel);

      if (sortByScore !== 0) {
        return sortByScore;
      }

      return 0;
    },
  );
}

export function sortDepartLegGroups(
  departLegGroup: DepartLegGroup[],
  sortType: FlightSortType,
  sortOrder: SortOrder,
) {
  switch (sortType) {
    case 'price':
      return sortGroupedTripViewModelBasedOnDepartLegByPrice(departLegGroup);
    case 'duration':
      return sortGroupedTripViewModelBasedOnDepartLegByDuration(departLegGroup);
    case 'score':
      return sortGroupedTripViewModelBasedOnDepartLegByScore(departLegGroup);
    case 'leg1_departure_time':
      return sortGroupedTripViewModelBasedOnDepartLegByDepartureTime(departLegGroup, sortOrder);
    default:
      return departLegGroup;
  }
}

export function getSelectedSortTypesForPerLegSearchFromParam(
  sortSearchParam: string,
  isShortDistanceRoute: boolean,
): FlightSortType[] {
  if (!!sortSearchParam) {
    return sortSearchParam.split(':').map((sortType) => {
      if (!!sortType) {
        return sortType as FlightSortType;
      }
      return isShortDistanceRoute ? 'price' : 'score';
    });
  }
  if (isShortDistanceRoute) {
    return ['price', 'price'];
  }
  return ['score', 'score'];
}

export function getSelectedSortOderForPerLegSearchFromParam(
  orderSearchParam: string,
  isShortDistanceRoute: boolean,
) {
  if (!!orderSearchParam) {
    return orderSearchParam.split(':').map((orderSearchParamForEachLeg) => {
      return !!orderSearchParamForEachLeg ? orderSearchParamForEachLeg : 'desc';
    });
  }

  if (isShortDistanceRoute) {
    return ['asc', 'asc'];
  }
  return ['desc', 'desc'];
}
